import { useState, FormEvent } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../services/api';
import { validatePassword, PasswordStrengthBar } from '../utils/passwordUtils';
import PasswordInput from '../components/Auth/PasswordInput';

export default function ResetPasswordPage() {
  const [searchParams]          = useSearchParams();
  const navigate                = useNavigate();
  const token                   = searchParams.get('token') ?? '';

  const [password, setPassword]         = useState('');
  const [confirm, setConfirm]           = useState('');
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState('');
  const [validationError, setValidation] = useState('');

  if (!token) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--background)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{
          background: '#fff', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)', padding: '44px 48px',
          boxShadow: 'var(--shadow-md)', textAlign: 'center', maxWidth: 400,
        }}>
          <p style={{ color: 'var(--destructive)', marginBottom: 20 }}>
            Érvénytelen vagy hiányzó visszaállítási token.
          </p>
          <Link to="/forgot-password" className="btn btn-primary" style={{ textDecoration: 'none' }}>
            Új link igénylése
          </Link>
        </div>
      </div>
    );
  }

  function validate(): boolean {
    const pwErr = validatePassword(password);
    if (pwErr) { setValidation(pwErr); return false; }
    if (password !== confirm) { setValidation('A két jelszó nem egyezik.'); return false; }
    setValidation('');
    return true;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    setError('');
    setLoading(true);
    try {
      await api.auth.resetPassword({ token, password });
      // Sikeres visszaállítás → navigálj a login oldalra üzenettel
      navigate('/login', { state: { message: 'A jelszó sikeresen megváltozott. Kérjük, jelentkezzen be.' } });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ismeretlen hiba.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--background)', display: 'flex', flexDirection: 'column' }}>

      {/* ── Hero header ── */}
      <div style={{
        background: 'linear-gradient(135deg, var(--primary) 0%, #064944 58%, var(--secondary) 150%)',
        padding: '32px 40px', display: 'flex', alignItems: 'center', gap: 18,
      }}>
        <img src="/logo_gymsmkik.svg" style={{ height: 40, width: 'auto' }} alt="GYMSMKIK" />
        <div style={{ borderLeft: '1px solid rgba(255,255,255,.3)', paddingLeft: 18 }}>
          <div style={{ fontWeight: 700, fontSize: 16, color: '#fff', lineHeight: 1.2 }}>
            Védjegy Digitalizációs Platform
          </div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,.72)', marginTop: 2 }}>
            GYMSM KIK, Védjegy pályázati portál
          </div>
        </div>
      </div>

      {/* ── Card ── */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px 20px' }}>
        <div style={{
          background: '#fff', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)', padding: '44px 48px',
          boxShadow: 'var(--shadow-md)', width: '100%', maxWidth: 440,
        }}>
          <div style={{ marginBottom: 32 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--secondary-foreground)', marginBottom: 8 }}>
              ÚJ JELSZÓ
            </div>
            <h1 style={{ margin: 0, fontSize: 26, fontWeight: 700, color: 'var(--primary)' }}>
              Jelszó visszaállítása
            </h1>
            <p style={{ margin: '8px 0 0', fontSize: 14, color: 'var(--muted-foreground)' }}>
              Adjon meg egy erős jelszót (min. 8 karakter, kis/nagybetű, szám, speciális karakter).
            </p>
          </div>

          {error && (
            <div className="alert alert-destructive" style={{ marginBottom: 20 }}>
              <span>⚠</span>
              <div><strong>Hiba</strong><p>{error}</p></div>
            </div>
          )}

          <form onSubmit={(e) => { void handleSubmit(e); }}>
            <div className="field">
              <label htmlFor="password">Új jelszó <span className="req">*</span></label>
              <PasswordInput
                id="password" autoComplete="new-password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setValidation(''); }}
                placeholder="Min. 8 kar., kis/nagybetű, szám, spec." required
              />
              <PasswordStrengthBar password={password} />
            </div>

            <div className="field" style={{ marginBottom: validationError ? 8 : 24 }}>
              <label htmlFor="confirm">Jelszó megerősítése <span className="req">*</span></label>
              <PasswordInput
                id="confirm" autoComplete="new-password"
                value={confirm}
                onChange={(e) => { setConfirm(e.target.value); setValidation(''); }}
                placeholder="••••••••" required
              />
            </div>

            {validationError && (
              <p style={{ color: 'var(--destructive)', fontSize: 13, marginBottom: 16 }}>
                {validationError}
              </p>
            )}

            <button
              type="submit"
              className="btn btn-primary"
              style={{ width: '100%', marginBottom: 16 }}
              disabled={loading}
            >
              {loading ? 'Mentés…' : 'Jelszó mentése →'}
            </button>
          </form>
        </div>
      </div>

      <div style={{ padding: '16px 40px', fontSize: 12, color: 'var(--muted-foreground)', textAlign: 'center' }}>
        © {new Date().getFullYear()} GYMSMKIK — Győr-Moson-Sopron Vármegyei Kereskedelmi és Iparkamara
      </div>
    </div>
  );
}
